import { useState } from 'react';
import DataTable from '../../reusable/DataTable/DataTable';
import SymbolFlag from '../../reusable/SymbolFlag/SymbolFlag';
import { NA } from '../../utilities/format';
import { useGetTradingJournalQuery } from '../../store/api/trademateApi';
import { t } from '../../i18n/i18n';

/** The journal's dates are midnight UTC — read in UTC so they don't slip a day west of it. */
const day = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : NA;

/** The API's empty strings and nulls both mean "not filled in". */
const text = (value) => (value == null || value === '' ? NA : String(value));

/** Notes were written in a rich-text editor, so they arrive as HTML — shown as its text. */
const plain = (html) => {
  if (!html) return NA;
  const out = new DOMParser().parseFromString(html, 'text/html').body.textContent.trim();
  return out || NA;
};

const yesNo = (value) => (value === true ? t('Yes') : value === false ? t('No') : NA);

const COLUMNS = [
  // Symbol and side drawn as Trade History draws them, so the two tables of
  // trades read alike.
  {
    key: 'symbol',
    header: 'Symbol',
    width: 14,
    render: (row) => (
      <span className="tm-symbol">
        {/* "EURUSD.pro" is the broker's symbol; the flag wants "EURUSD". */}
        <SymbolFlag symbol={String(row.symbol ?? '').split('.')[0]} />
        <span className="tm-symbol__text">{text(row.symbol)}</span>
      </span>
    ),
  },
  {
    key: 'trade_type',
    header: 'Trade type',
    width: 10,
    value: (row) => t(text(row.trade_type)),
    tone: (row) => (row.trade_type === 'Sell' ? 'sell' : row.trade_type === 'Buy' ? 'buy' : undefined),
  },
  { key: 'entry', header: 'Entry', width: 8, value: (row) => t(text(row.entry)) },
  { key: 'size', header: 'Size', width: 8, value: (row) => text(row.size) },
  {
    key: 'date',
    header: 'Date',
    width: 12,
    sortable: true,
    value: (row) => day(row.date),
    sortValue: (row) => row.date ?? '',
  },
  {
    key: 'profit',
    header: 'Profit',
    width: 10,
    align: 'right',
    value: (row) => text(row.profit),
    tone: (row) => (Number(row.profit) > 0 ? 'up' : Number(row.profit) < 0 ? 'down' : 'muted'),
  },
];

/** One block of the expanded plan: a heading over label/value pairs. */
function PlanGroup({ title, fields }) {
  return (
    <section className="tm-plan__group">
      <h3 className="tm-plan__title">{t(title)}</h3>
      <dl className="tm-plan__fields">
        {fields.map(([label, value]) => (
          <div key={label} className="tm-plan__field">
            <dt>{t(label)}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * The trade journal — live's Trade Journal tab, read-only as live has it (its
 * create form is switched off). Each row opens to the plan written for it.
 *
 * Live's expanded row reads every field as "N/A": it hands the table's row
 * wrapper to the details panel instead of the entry, so `counter_trend`,
 * `notes` and the rest are never found. Here the entry itself is read.
 */
export default function TradeJournal() {
  const { data, isFetching, isError } = useGetTradingJournalQuery();
  const [openId, setOpenId] = useState(null);

  const toggle = (row) => setOpenId((prev) => (prev === row.id ? null : row.id));

  const columns = [
    ...COLUMNS,
    {
      key: 'action',
      header: '',
      width: 8,
      align: 'right',
      searchable: false,
      render: (row) => (
        <span className="tm-journal__toggle" aria-expanded={openId === row.id}>
          {openId === row.id ? t('Hide plan') : t('View plan')}
        </span>
      ),
    },
  ];

  const renderExpanded = (row) => (
    <div className="tm-plan">
      <PlanGroup
        title="Setup"
        fields={[
          ['Counter-trend trade', yesNo(row.counter_trend)],
          ['Trigger', text(row.trigger)],
          ['Supporting evidence', text(row.supporting_evidence)],
          ['Opposing evidence', text(row.opposing_evidence)],
          ['Heads up', text(row.heads_up)],
        ]}
      />
      <PlanGroup
        title="Entry plan"
        fields={[
          ['Entry order price', text(row.order_price)],
          ['Max stop price', text(row.max_stop_price)],
          ['Size', text(row.size)],
          ['Max $ loss', text(row.max_loss)],
        ]}
      />
      <PlanGroup
        title="Exit plan"
        fields={[
          ['Technical stop', text(row.technical_stop)],
          ['Exit target / trigger', text(row.exit_traget)],
          ['Time stop', text(row.time_stop)],
          ['Est. risk / reward', text(row.risk_or_reward)],
        ]}
      />
      <section className="tm-plan__group tm-plan__group--notes">
        <h3 className="tm-plan__title">{t('Notes, insights & results')}</h3>
        <p className="tm-plan__notes">{plain(row.notes)}</p>
      </section>
    </div>
  );

  return (
    <DataTable
      className="tm-journal"
      title="Trade journal"
      count={data?.journal?.length}
      icon="nav-trades"
      columns={columns}
      rows={data?.journal ?? []}
      minWidth={760}
      loading={isFetching && !data}
      emptyMessage={isError ? 'Could not load your trade journal' : 'No journal entries yet'}
      onRowClick={toggle}
      expandedId={openId}
      renderExpanded={renderExpanded}
    />
  );
}
